import React, { useCallback, useEffect, useState } from "react";
import { ServerData } from "../data/ServerData";
import type { DrawingSummary } from "../data/ServerData";

import "./DrawingPicker.scss";

interface DrawingPickerProps {
  isOpen: boolean;
  onClose: () => void;
  onOpenDrawing: (drawingId: string) => void;
  onNewDrawing: () => void;
  currentDrawingId: string | null;
}

export const DrawingPicker: React.FC<DrawingPickerProps> = ({
  isOpen,
  onClose,
  onOpenDrawing,
  onNewDrawing,
  currentDrawingId,
}) => {
  const [drawings, setDrawings] = useState<DrawingSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [renamingId, setRenamingId] = useState<string | null>(null);
  const [renameValue, setRenameValue] = useState("");

  const loadDrawings = useCallback(async () => {
    try {
      setLoading(true);
      const list = await ServerData.list();
      setDrawings(list);
    } catch (err) {
      console.error("Failed to load drawings:", err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (isOpen) {
      loadDrawings();
    }
  }, [isOpen, loadDrawings]);

  const handleDelete = async (id: string, name: string) => {
    if (!confirm(`Delete "${name}"? This cannot be undone.`)) {
      return;
    }
    try {
      await ServerData.delete(id);
      setDrawings((prev) => prev.filter((d) => d.id !== id));
    } catch (err) {
      console.error("Failed to delete drawing:", err);
    }
  };

  const handleRename = async (id: string) => {
    if (!renameValue.trim()) {
      setRenamingId(null);
      return;
    }
    try {
      await ServerData.rename(id, renameValue.trim());
      setDrawings((prev) =>
        prev.map((d) =>
          d.id === id ? { ...d, name: renameValue.trim() } : d,
        ),
      );
    } catch (err) {
      console.error("Failed to rename drawing:", err);
    }
    setRenamingId(null);
  };

  if (!isOpen) {
    return null;
  }

  return (
    <div className="drawing-picker-overlay" onClick={onClose}>
      <div
        className="drawing-picker-modal"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="drawing-picker-header">
          <h2>My Drawings</h2>
          <div className="drawing-picker-actions">
            <button className="drawing-picker-new-btn" onClick={onNewDrawing}>
              + New Drawing
            </button>
            <button className="drawing-picker-close-btn" onClick={onClose}>
              &times;
            </button>
          </div>
        </div>

        <div className="drawing-picker-content">
          {loading ? (
            <div className="drawing-picker-loading">Loading...</div>
          ) : drawings.length === 0 ? (
            <div className="drawing-picker-empty">
              <p>No saved drawings yet.</p>
              <p>Create your first drawing to get started!</p>
            </div>
          ) : (
            <div className="drawing-picker-grid">
              {drawings.map((drawing) => (
                <div
                  key={drawing.id}
                  className={`drawing-picker-card ${
                    drawing.id === currentDrawingId ? "active" : ""
                  }`}
                >
                  <div
                    className="drawing-picker-card-preview"
                    onClick={() => {
                      onOpenDrawing(drawing.id);
                      onClose();
                    }}
                  >
                    {drawing.thumbnail ? (
                      <img src={drawing.thumbnail} alt={drawing.name} />
                    ) : (
                      <div className="drawing-picker-card-placeholder">
                        <svg
                          width="48"
                          height="48"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="1.5"
                        >
                          <rect x="3" y="3" width="18" height="18" rx="2" />
                          <line x1="9" y1="3" x2="9" y2="21" />
                          <line x1="3" y1="9" x2="21" y2="9" />
                        </svg>
                      </div>
                    )}
                  </div>
                  <div className="drawing-picker-card-info">
                    {renamingId === drawing.id ? (
                      <input
                        className="drawing-picker-rename-input"
                        value={renameValue}
                        onChange={(e) => setRenameValue(e.target.value)}
                        onBlur={() => handleRename(drawing.id)}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") {
                            handleRename(drawing.id);
                          }
                          if (e.key === "Escape") {
                            setRenamingId(null);
                          }
                        }}
                        autoFocus
                      />
                    ) : (
                      <span
                        className="drawing-picker-card-name"
                        onDoubleClick={() => {
                          setRenamingId(drawing.id);
                          setRenameValue(drawing.name);
                        }}
                        title="Double-click to rename"
                      >
                        {drawing.name}
                      </span>
                    )}
                    <span className="drawing-picker-card-date">
                      {new Date(drawing.updated_at + "Z").toLocaleDateString(
                        undefined,
                        {
                          month: "short",
                          day: "numeric",
                          hour: "2-digit",
                          minute: "2-digit",
                        },
                      )}
                    </span>
                  </div>
                  <button
                    className="drawing-picker-card-delete"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDelete(drawing.id, drawing.name);
                    }}
                    title="Delete drawing"
                  >
                    &times;
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
